import type {
  HealthBandsDef,
  HealthBoundsDef,
  HealthHrBandsDef,
  HealthLevelsDef,
  HealthRulesDef,
  HealthTrustedSourceDef,
} from './types';

/*
 * The C# initializers of shared-cs `HealthRulesDef` and its blocks (health intake CONTRACT §7), for a draft without the section or a
 * block. Empty `tierCredit`, `intensityClamp`, `mets` and `trustedSources` mean "use the C# defaults" on the server; the `DEFAULT_*`
 * values below are those defaults (`HealthRulesDef.DefaultTierCredit`, `DefaultMets()`, `DefaultTrustedSources()`), which the editor
 * shows and copies in with its "Fill in the defaults" buttons. Keep in step with HealthRulesDef.cs.
 */
export const hrBandsDefaults = (): HealthHrBandsDef => ({
  easyBelow: 0.6,
  hardAbove: 0.75,
  easyMult: 0.6,
  moderateMult: 1,
  hardMult: 1.4,
});
export const boundsDefaults = (): HealthBoundsDef => ({
  runMaxKmh: 25,
  walkMaxKmh: 8,
  cycleMaxKmh: 60,
  hrOverMaxBpm: 10,
  hrFlatBpm: 3,
  hrFlatMinSamples: 20,
  kcalPerMinMax: 20,
  defaultMaxHr: 180,
});
export const bandsDefaults = (): HealthBandsDef => ({ light: 10, moderate: 30, vigorous: 60 });
export const levelsDefaults = (): HealthLevelsDef => ({
  walker: 30,
  mover: 120,
  athlete: 300,
  beast: 600,
});

export const healthRulesDefaults = (): HealthRulesDef => ({
  enabled: true,
  readDailies: false,
  // WIND (contract 3.7): the lowest workout trust tier that counts toward the wind level, and the level's wind numbers
  // (empty lists = shared-cs HealthLevelWindDef.DefaultRecovery / DefaultRest, lib/wind/defaults.ts healthWindDefaults).
  levelMinTier: 2,
  levelWind: { recovery: [], rest: [] },
  importDays: 30,
  syncIntervalMin: 10,
  maxWorkoutsPerDay: 4,
  maxEffortMinPerDay: 120,
  maxWorkoutSec: 14400,
  maxDayWorkoutMin: 480,
  lateDays: 30,
  futureSlackSec: 300,
  tierCredit: [],
  mets: {},
  hrBands: hrBandsDefaults(),
  metIntensityDivisor: 6,
  intensityClamp: [],
  bounds: boundsDefaults(),
  overlapFraction: 0.5,
  hrCoverageForTier3: 0.6,
  bands: bandsDefaults(),
  levels: levelsDefaults(),
  trustedSources: [],
  powerFamily: 'powered_up',
});

export const DEFAULT_TIER_CREDIT = [0, 0.4, 0.7, 1] as const;
export const DEFAULT_INTENSITY_CLAMP = [0.5, 1.5] as const;
export const DEFAULT_METS: Readonly<Record<string, number>> = {
  walk: 4.5,
  run: 9,
  cycle: 7,
  hike: 6,
  strength: 5,
  hiit: 8,
  swim: 7,
  sport: 6,
  other: 4,
};
export const defaultTrustedSources = (): HealthTrustedSourceDef[] => [
  { id: 'com.apple.health', platform: 'ios', tier: 3 },
  { id: 'com.apple.Health', platform: 'ios', tier: 0 },
  { id: 'com.garmin.connect.mobile', platform: 'ios', tier: 2 },
  { id: 'com.garmin.android.apps.connectmobile', platform: 'android', tier: 3 },
  { id: 'com.strava.stravaride', platform: 'ios', tier: 2 },
  { id: 'com.strava', platform: 'android', tier: 2 },
  { id: 'com.sec.android.app.shealth', platform: 'android', tier: 3 },
  { id: 'com.fitbit.FitbitMobile', platform: 'ios', tier: 3 },
  { id: 'com.fitbit.FitbitMobile', platform: 'android', tier: 3 },
  { id: 'com.nike.nikeplus-gps', platform: 'ios', tier: 2 },
  { id: 'com.nike.plusgps', platform: 'android', tier: 2 },
  { id: 'com.onepeloton.callisto', platform: 'ios', tier: 2 },
  { id: 'com.onepeloton.callisto', platform: 'android', tier: 2 },
];

const dropUndefined = <T extends object>(v: T) =>
  Object.fromEntries(Object.entries(v).filter(([, x]) => x !== undefined)) as T;

/** A nested block as the server parses it: absent = its defaults, explicit null = null, else merged over the defaults. */
const block = <T extends object>(d: () => T, v: T | null | undefined): T | null =>
  v === undefined ? d() : v === null ? null : { ...d(), ...dropUndefined(v) };

/** `healthRules` as the server parses it (absent fields and blocks filled with the C# initializers). Null stays null. */
export function healthAsParsed(h: HealthRulesDef | null | undefined): HealthRulesDef | null {
  if (h === null) return null;
  const v = h ?? {};
  return {
    ...healthRulesDefaults(),
    ...dropUndefined(v),
    hrBands: block(hrBandsDefaults, v.hrBands),
    bounds: block(boundsDefaults, v.bounds),
    bands: block(bandsDefaults, v.bands),
    levels: block(levelsDefaults, v.levels),
  };
}
